import { EchartsWithTheme, useTheme } from '@maxtropy/components';
import { apiV2OperationAnalysisCurtailmentPost } from '@maxtropy/dmes-apis-v2';
import { useRequest } from 'ahooks';
import { Progress } from 'antd';
import { useMemo, useState } from 'react';
import { getCurtailmentOption } from '../../chartOptions';
import { formatValue } from '../../utils';
import AnalysisToolbar, { AnalysisFilterValue } from '../AnalysisToolbar';
import SectionHeader from '../SectionHeader';
import styles from './index.module.scss';

const getProgressPercent = (value: number | undefined) => Math.min(100, Math.max(0, value ?? 0));

const exportCurtailment = ({ granularity, range }: AnalysisFilterValue) => {
  const query = new URLSearchParams({
    timestamp: String(Date.now()),
    dimension: granularity.toUpperCase(),
    from: String(range[0].startOf(granularity).valueOf()),
    to: String(range[1].endOf(granularity).valueOf()),
  });
  window.open(`/api/v2/operation-analysis/curtailment/export?${query.toString()}`, '_blank');
};

const CurtailmentAnalysis = () => {
  const theme = useTheme();
  const [filter, setFilter] = useState<AnalysisFilterValue>();
  const { data } = useRequest(
    () => {
      const currentFilter = filter!;
      const { granularity, range } = currentFilter;
      return apiV2OperationAnalysisCurtailmentPost({
        timestamp: Date.now(),
        dimension: granularity.toUpperCase(),
        from: range[0].startOf(granularity).valueOf(),
        to: range[1].endOf(granularity).valueOf(),
      });
    },
    {
      ready: !!filter,
      refreshDeps: [filter],
    }
  );
  const baselineData = data?.baselineData;
  const actualData = data?.actualData;
  const showBaselineCard = filter?.granularity !== 'day';
  const chartOption = useMemo(() => getCurtailmentOption(data?.trendData ?? []), [data?.trendData]);

  return (
    <section className={styles.section}>
      <SectionHeader title="弃光分析" />
      <AnalysisToolbar enforceRangeRules onChange={setFilter} onExport={exportCurtailment} />
      <div className={`${styles.summaryRow} ${showBaselineCard ? '' : styles.singleCard}`}>
        {showBaselineCard && (
          <div className={styles.summaryCard}>
            <span className={styles.cardTitle}>基准数据 (2025-01~2025-12)</span>
            <div className={styles.cardValues}>
              <div>
                <span>弃光量</span>
                <strong>{formatValue(baselineData?.curtailmentAmount, 'MWh')}</strong>
              </div>
              <div>
                <span>弃光率</span>
                <strong>{formatValue(baselineData?.curtailmentRate, '%')}</strong>
              </div>
              <Progress
                type="circle"
                width={24}
                strokeWidth={10}
                percent={getProgressPercent(baselineData?.curtailmentRate)}
                strokeColor="#32df78"
                trailColor="var(--mx-light-border-color)"
                showInfo={false}
              />
            </div>
          </div>
        )}
        <div className={styles.summaryCard}>
          <span className={styles.cardTitle}>实际运行</span>
          <div className={styles.cardValues}>
            <div>
              <span>弃光量</span>
              <strong>{formatValue(actualData?.curtailmentAmount, 'MWh')}</strong>
            </div>
            <div>
              <span>弃光率</span>
              <strong>{formatValue(actualData?.curtailmentRate, '%')}</strong>
            </div>
            <Progress
              type="circle"
              width={24}
              strokeWidth={10}
              percent={getProgressPercent(actualData?.curtailmentRate)}
              strokeColor="#32df78"
              trailColor="var(--mx-light-border-color)"
              showInfo={false}
            />
          </div>
        </div>
      </div>
      <EchartsWithTheme theme={theme} option={chartOption} style={{ height: 280, marginTop: 16 }} />
    </section>
  );
};

export default CurtailmentAnalysis;
